import { Checkbox, Col, Row, Form } from 'antd';
import { Field, FieldProps, useFormikContext } from 'formik';
import { Controller, useFormContext } from 'react-hook-form';
import React from 'react';
import { useTranslation } from 'react-i18next';

const CheckboxGroup = Checkbox.Group;

export const getCheckable = (elems: Elements[]) =>
  elems.filter(({ disabled }) => !disabled).map(({ id }) => id);

type Elements = {
  id: string;
  name: string;
  disabled?: boolean;
};

type BaseCheckboxGroupProps = {
  name: string;
  elements: Elements[];
};

type FormCheckboxGroupProps = BaseCheckboxGroupProps & {
  className?: string;
  label?: React.ReactNode;
  required?: boolean;
  useHookForm?: boolean;
};

type HookFormModeProps = BaseCheckboxGroupProps & {
  className?: string;
  label?: React.ReactNode;
  required?: boolean;
  checkableElements: string[];
};

type FormikModeProps = BaseCheckboxGroupProps & {
  checkableElements: string[];
};

// Internal component for react-hook-form - hooks called at top level
const HookFormMode: React.FC<HookFormModeProps> = ({
  name,
  elements,
  className,
  label,
  required,
  checkableElements,
}) => {
  const { t } = useTranslation();
  const { control, setValue, getValues } = useFormContext();

  const onCheckAllChange = () => {
    const selected = getValues(name);
    if (checkableElements.length === selected.length) {
      setValue(name, []);
    } else {
      setValue(name, checkableElements);
    }
  };

  const onChange = (selected: string[]) => {
    setValue(name, selected);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Form.Item
          className={className}
          label={label}
          required={required}
          validateStatus={error ? 'error' : ''}
          help={error?.message || undefined}
        >
          <div className="forms-checkbox-group">
            <Checkbox
              indeterminate={
                field.value.length > 0 && field.value.length < checkableElements.length
              }
              onChange={onCheckAllChange}
              checked={field.value.length === checkableElements.length}
            >
              {t('common:selectAll')}
            </Checkbox>
          </div>
          <CheckboxGroup onChange={onChange} value={field.value}>
            <Row>
              {elements.map(({ id, name: checkboxName, disabled }) => (
                <Col span={12} key={id}>
                  <Checkbox value={id} disabled={disabled}>
                    {checkboxName}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </CheckboxGroup>
        </Form.Item>
      )}
    />
  );
};

// Internal component for Formik - hooks called at top level
const FormikMode: React.FC<FormikModeProps> = ({ name, elements, checkableElements }) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext<any>();

  const onCheckAllChange = (selected: string[]) => {
    if (checkableElements.length === selected.length) {
      setFieldValue(name, []);
    } else {
      setFieldValue(name, checkableElements);
    }
  };

  const onChange = (e: string[]) => {
    setFieldValue(name, e);
  };

  const fieldContent = ({ field }: FieldProps) => (
    <div>
      <div className="forms-checkbox-group">
        <Checkbox
          indeterminate={field.value.length > 0 && field.value.length < checkableElements.length}
          onChange={() => onCheckAllChange(field.value)}
          checked={field.value.length === checkableElements.length}
        >
          {t('common:selectAll')}
        </Checkbox>
      </div>
      <CheckboxGroup onChange={onChange} value={field.value}>
        <Row>
          {elements.map(({ id, name: checkboxName, disabled }, i) => (
            <Col span={12} key={i}>
              <Checkbox value={id} disabled={disabled}>
                {checkboxName}
              </Checkbox>
            </Col>
          ))}
        </Row>
      </CheckboxGroup>
    </div>
  );

  return <Field name={name}>{fieldContent}</Field>;
};

// Main exported component - no conditional hooks, just conditional rendering
const FormCheckboxGroup: React.FC<FormCheckboxGroupProps> = props => {
  const checkableElements = React.useMemo(() => getCheckable(props.elements), [props.elements]);

  if (props.useHookForm) {
    return <HookFormMode {...props} checkableElements={checkableElements} />;
  }

  return <FormikMode {...props} checkableElements={checkableElements} />;
};

export default FormCheckboxGroup;
